'use client';

import { useEffect, useState } from 'react';
import type { DocsSearchEntry } from './search-index';

let cache: DocsSearchEntry[] | null = null;
let inFlight: Promise<DocsSearchEntry[]> | null = null;

function load(): Promise<DocsSearchEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = fetch('/api/docs-index')
      .then((r) => (r.ok ? r.json() : []))
      .then((entries: DocsSearchEntry[]) => {
        cache = entries;
        return entries;
      })
      .catch(() => [] as DocsSearchEntry[])
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

export function useDocsSearchIndex(enabled: boolean): DocsSearchEntry[] {
  const [entries, setEntries] = useState<DocsSearchEntry[]>(() => cache ?? []);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    load().then((loaded) => {
      if (live) setEntries(loaded);
    });
    return () => {
      live = false;
    };
  }, [enabled]);

  return entries;
}

export interface DocsSearchResult {
  label: string;
  context: string;
  href: string;
}

export function searchDocs(
  entries: DocsSearchEntry[],
  query: string,
  cap: number,
): DocsSearchResult[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const out: DocsSearchResult[] = [];
  for (const entry of entries) {
    if (entry.title.toLowerCase().includes(needle)) {
      out.push({ label: entry.title, context: 'Documentation', href: entry.urlPath });
    }
    for (const heading of entry.headings) {
      if (heading.text.toLowerCase().includes(needle)) {
        out.push({
          label: heading.text,
          context: entry.title,
          href: `${entry.urlPath}#${heading.id}`,
        });
      }
      if (out.length >= cap) return out;
    }
    if (out.length >= cap) return out;
  }
  return out;
}
